import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    Stack,
    Typography
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './Activity.css';

const sleepHours = [
    6.7, 8.0, 8.0, 8.3, 7.8, 8.5, 7.4, 7.4, 7.3, 7.4,
    7.8, 7.4, 7.3, 8.4, 7.9, 7.7, 7.3, 7.7, 8.0, 8.1,
    7.3, 7.5, 7.6, 7.7, 7.7, 7.8, 7.7, 7.4, 7.4, 7.3
];

const weeklyActivity = [2.4, 2.4, 1.6, 2.0, 2.0, 2.2, 2.4];
const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const TrendMetric = ({ direction, change, label, value, tone }) => {
    const isDown = direction === 'down';

    return (
        <Stack direction="row" spacing={1.5} alignItems="center" className="trend-metric">
            <Box className={`trend-badge trend-badge--${tone}`}>
                {isDown ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                <Typography component="span">{change}</Typography>
            </Box>
            <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    {label}
                </Typography>
                <Typography className={`trend-value trend-value--${tone}`}>{value}</Typography>
            </Box>
        </Stack>
    );
};

const SleepChart = () => (
    <Box className="chart-shell sleep-chart" role="img" aria-label="Demo chart of average household sleep over 30 days">
        <Box className="chart-grid-lines" aria-hidden="true" />
        <Box className="sleep-bars">
            {sleepHours.map((hours, index) => (
                <Box
                    key={index}
                    className="sleep-bar"
                    sx={{ height: `${(hours / 10) * 100}%` }}
                    title={`Day ${index + 1}: ${hours} hours`}
                />
            ))}
        </Box>
        <Box className="chart-y-axis">
            <span>10 hr</span>
            <span>5 hr</span>
            <span>0 hr</span>
        </Box>
        <Box className="sleep-x-axis">
            <span>1</span><span>8</span><span>15</span><span>22</span><span>29</span>
        </Box>
    </Box>
);

const ActivityChart = () => (
    <Box className="chart-shell activity-chart" role="img" aria-label="Demo chart of average household activity by day">
        <Box className="chart-grid-lines" aria-hidden="true" />
        <Box className="goal-line"><span>Goal</span></Box>
        <Box className="activity-bars">
            {weeklyActivity.map((hours, index) => (
                <Box className="activity-bar-column" key={`${weekDays[index]}-${index}`}>
                    <Box
                        className="activity-bar"
                        sx={{ height: `${(hours / 4) * 100}%` }}
                        title={`${weekDays[index]}: ${hours} hours`}
                    />
                    <span>{weekDays[index]}</span>
                </Box>
            ))}
        </Box>
        <Box className="chart-y-axis activity-y-axis">
            <span>4 hr</span>
            <span>2 hr</span>
            <span>0 hr</span>
        </Box>
    </Box>
);

const Activity = () => {
    const navigate = useNavigate();
    const activeUnit = useSelector(state => state.auth.activeUnit);
    const customerName = activeUnit?.CustomerAlias ||
        [activeUnit?.FirstName, activeUnit?.LastName].filter(Boolean).join(' ') ||
        'Selected customer';

    return (
        <Container maxWidth="lg" className="activity-page">
            <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{ mb: 2 }}
            >
                Back to Status
            </Button>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                mb={3}
            >
                <Box>
                    <Typography variant="h4" component="h1" fontWeight={800}>Activity</Typography>
                    <Typography color="text.secondary">Health and wellness insights for {customerName}</Typography>
                </Box>
            </Stack>

            <Card className="activity-card trends-card">
                <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
                        <ShowChartIcon color="action" />
                        <Typography variant="h5">30 Day Trends</Typography>
                    </Stack>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                            <TrendMetric direction="down" change="15 min" label="Activity Duration" value="2hr 15min" tone="cyan" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TrendMetric direction="down" change="15 min" label="Sleep Duration" value="7hr 20min" tone="indigo" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TrendMetric direction="up" change="2" label="Sleep Interruptions" value="1.7" tone="indigo" />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={3} mt={0}>
                <Grid item xs={12} md={7}>
                    <Card className="activity-card insight-card">
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                <BedtimeIcon className="sleep-icon" />
                                <Typography variant="h6">Avg Household Sleep</Typography>
                            </Stack>
                            <Grid container alignItems="center" spacing={2} mb={2}>
                                <Grid item xs={12} sm={4}>
                                    <Typography className="primary-stat">8<span>hrs</span> 3<span>mins</span></Typography>
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <Typography color="text.secondary" fontWeight={700}>Bedtime</Typography>
                                    <Typography variant="h6">10:25 PM</Typography>
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <Typography color="text.secondary" fontWeight={700}>Wake time</Typography>
                                    <Typography variant="h6">6:29 AM</Typography>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Box display="flex" justifyContent="center" mt={2}>
                                <Chip label="1 Avg. Interruption" />
                            </Box>
                            <SleepChart />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Card className="activity-card insight-card">
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <DirectionsWalkIcon className="activity-icon" />
                                <Typography variant="h6">Avg Household Activity</Typography>
                            </Stack>
                            <Typography className="primary-stat activity-primary-stat">1<span>hr</span> 35<span>min</span></Typography>
                            <ActivityChart />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default Activity;
